import {useLayoutEffect, useRef} from 'react';

const MAX_LINES = 2;
const PRECISION = 0.5;
const HARD_MIN_FONT = 8;

const cssPx = (styles: CSSStyleDeclaration, token: string, fallback: number) =>
    parseFloat(styles.getPropertyValue(token)) || fallback;

// Shrinks the value font (starting from --font-size-text-metric-secondary) until the text fits within
// MAX_LINES. Text that already fits keeps the full size untouched — a single measurement, no style churn.
// Overflow is detected by comparing scrollHeight to line-height × MAX_LINES (scrollHeight reports the full
// content height even when clipped), so it works whether or not the CSS keeps overflow: hidden. The search
// is binary (~6 reflows worst case, one when the text fits) and re-runs are coalesced to one per frame, so
// resize storms don't thrash layout. A ResizeObserver on the element catches container changes (sidebar,
// flex reflow) that window resize would miss; re-running after document.fonts.ready covers the web font
// swapping in wider than the fallback the first pass measured. Only below HARD_MIN_FONT — where the text
// would be unreadable anyway — does the line-clamp ellipsis remain.
export default function useShrinkToFit(text: string) {
    const ref = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const element = ref.current;
        if(!element) return;

        const styles = getComputedStyle(element);
        const maxFont = cssPx(styles, '--font-size-text-metric-secondary', 20);
        const lineHeightRatio = parseFloat(styles.lineHeight) / parseFloat(styles.fontSize) || 1.2;
        // clientWidth includes padding but the observer's contentRect doesn't — subtract it so the
        // stale-width guard compares content-box to content-box.
        const paddingX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);

        const overflows = (size: number) => {
            element.style.fontSize = `${size}px`;
            return element.scrollHeight > size * lineHeightRatio * MAX_LINES + 1;
        };

        let lastWidth = 0;
        const fit = () => {
            if(overflows(maxFont)) {
                let low = HARD_MIN_FONT;
                let high = maxFont;
                while(high - low > PRECISION) {
                    const mid = (low + high) / 2;
                    if(overflows(mid)) high = mid;
                    else low = mid;
                }
                element.style.fontSize = `${low}px`;
            }
            lastWidth = element.clientWidth - paddingX;
        };

        fit();

        let frame = 0;
        const scheduleFit = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(fit);
        };

        // Font-size writes inside fit() resize the element and re-trigger the observer; comparing against
        // the width recorded at the end of the last fit stops that feedback loop.
        const observer = new ResizeObserver(entries => {
            const {width} = entries[entries.length - 1].contentRect;
            if(Math.round(width) !== Math.round(lastWidth)) scheduleFit();
        });
        observer.observe(element);

        let cancelled = false;
        document.fonts?.ready.then(() => !cancelled && scheduleFit());

        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [text]);

    return ref;
}
